import React, { useEffect, useState } from 'react';
import { FlatSprout } from '../components/FlatSprout';
import { FlatTree } from '../components/FlatTree';
import { RomaMapWithTrees } from '../components/RomaMapWithTrees';
import { TreePine, ArrowRight } from 'lucide-react';
interface Props {
  onContinue: () => void;
}
export function OnboardingSprout({
  onContinue
}: Props) {
  const [slide, setSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [slideAnimReady, setSlideAnimReady] = useState(false);
  const totalSlides = 5;
  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
      setSlideAnimReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  const next = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setSlideAnimReady(false);
    setTimeout(() => {
      if (slide < totalSlides - 1) {
        setSlide(slide + 1);
      } else {
        onContinue();
      }
      setTimeout(() => {
        setIsTransitioning(false);
        setTimeout(() => setSlideAnimReady(true), 50);
      }, 40);
    }, 180);
  };
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] px-6 pt-6 pb-6 relative overflow-hidden" data-id="element-402">
      {/* Background video — only on slide 0 */}
      {slide === 0 && <>
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700" style={{
        opacity: slideAnimReady ? 0.35 : 0
      }} data-id="element-403">
            <source src="https://video.wixstatic.com/video/dc6efb_f97d8ca171e342a39937f8fa5adb1258/720p/mp4/file.mp4" type="video/mp4" data-id="element-404" />
          </video>
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0D0D0D]/60 via-transparent to-[#0D0D0D]" data-id="element-405" />
        </>}
      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mb-8 transition-all duration-700 ease-out relative z-10" style={{
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)'
    }} data-id="element-406">
        {Array.from({
        length: totalSlides
      }).map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? 'w-6 bg-[#C5D82D]' : 'w-1.5 bg-[#2A2A2A]'}`} data-id="element-407" />)}
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10" data-id="element-408">
        <div className="transition-all duration-200 ease-in-out w-full flex flex-col items-center" style={{
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning ? 'translateY(-20px)' : 'translateY(0)'
      }} data-id="element-409">
          {/* Slide 0 — Benvenuto */}
          {slide === 0 && <div className="flex flex-col items-center text-center" data-id="element-410">
              {/* Visual area */}
              <div className="flex flex-col items-center justify-center transition-all duration-600 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '0ms',
            transitionDuration: '700ms'
          }} data-id="element-411">
                <img src="https://static.wixstatic.com/media/6e7739_2497b1d974164d3ebd4c4ad72f6d0b73~mv2.png/v1/crop/x_0,y_21,w_4505,h_1713/fill/w_266,h_100,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%20EIIS_bianco.png" alt="EIIS" className="h-12 object-contain" data-id="element-412" />
                <h1 className="font-medium text-white tracking-tight mt-3 text-[24px]" data-id="element-413">
                  Summit 2026
                </h1>
              </div>
              {/* Text area */}
              <div className="flex flex-col items-center gap-3 mt-3 transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '150ms'
          }} data-id="element-414">
                <p className="text-[16px] text-white/90 max-w-[300px] leading-relaxed text-center" data-id="element-415">
                  Connettiti, crea impatto e pianta il tuo albero.
                </p>
              </div>
            </div>}

          {/* Slide 1 — Il tuo seme */}
          {slide === 1 && <div className="flex flex-col items-center text-center" data-id="element-416">
              <div className="h-[180px] flex items-center justify-center transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '0ms'
          }} data-id="element-417">
                <FlatSprout data-id="element-418" />
              </div>
              <div className="flex flex-col items-center gap-3 mt-8 transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '150ms'
          }} data-id="element-419">
                <h1 className="text-[24px] font-medium text-white tracking-tight" data-id="element-420">
                  Questo è il tuo seme
                </h1>
                <p className="text-[14px] text-[#888888] max-w-[280px] leading-relaxed" data-id="element-421">
                  È stato appena piantato grazie alla tua partecipazione al
                  Summit.
                </p>
              </div>
            </div>}

          {/* Slide 2 — L'albero */}
          {slide === 2 && <div className="flex flex-col items-center text-center" data-id="element-422">
              <div className="h-[180px] flex items-center justify-center transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '0ms'
          }} data-id="element-423">
                <FlatTree data-id="element-424" />
              </div>
              <div className="flex flex-col items-center gap-3 mt-8 transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '150ms'
          }} data-id="element-425">
                <h1 className="text-[24px] font-medium text-white tracking-tight" data-id="element-426">
                  Diventerà un albero vero
                </h1>
                <p className="text-[14px] text-[#888888] max-w-[280px] leading-relaxed" data-id="element-427">
                  Ogni seme piantato durante il Summit crescerà e diventerà un
                  albero reale.
                </p>
              </div>
            </div>}

          {/* Slide 3 — Come farlo crescere */}
          {slide === 3 && <div className="flex flex-col items-center text-center" data-id="element-428">
              <div className="h-[140px] flex items-center justify-center transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '0ms'
          }} data-id="element-429">
                <div className="relative" data-id="element-430">
                  <div className="relative flex items-center justify-center" data-id="element-431">
                    <div className="absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-[#0D0D0D] to-transparent pointer-events-none" data-id="element-432" />
                    <div className="absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-[#0D0D0D] to-transparent pointer-events-none" data-id="element-433" />
                    <div className="flex items-center -space-x-5" data-id="element-434">
                      <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=160&h=160&fit=crop&crop=face" alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[#0D0D0D] opacity-60" data-id="element-435" />
                      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=face" alt="" className="w-28 h-28 rounded-full object-cover border-2 border-[#0D0D0D] relative z-[5]" data-id="element-436" />
                      <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=160&h=160&fit=crop&crop=face" alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[#0D0D0D] opacity-60" data-id="element-437" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-3 mt-3 transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '150ms'
          }} data-id="element-438">
                <h1 className="text-[24px] font-medium text-white tracking-tight" data-id="element-439">
                  Come? Creando connessioni.
                </h1>
                <p className="text-[14px] text-[#888888] max-w-[280px] leading-relaxed" data-id="element-440">
                  Seleziona i temi che ti interessano e la nostra AI troverà le
                  persone giuste con cui parlare. Ogni connessione fa crescere
                  il tuo albero.
                </p>
              </div>
            </div>}

          {/* Slide 4 — Impatto reale */}
          {slide === 4 && <div className="flex flex-col items-center text-center" data-id="element-441">
              <div className="h-[180px] flex items-center justify-center w-full transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '0ms'
          }} data-id="element-442">
                <RomaMapWithTrees data-id="element-443" />
              </div>
              <div className="flex flex-col items-center gap-3 mt-8 transition-all duration-700 ease-out" style={{
            opacity: slideAnimReady ? 1 : 0,
            transform: slideAnimReady ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '150ms'
          }} data-id="element-444">
                <h1 className="text-[24px] font-medium text-white tracking-tight" data-id="element-445">
                  Un impatto reale
                </h1>
                <p className="text-[14px] text-[#888888] max-w-[280px] leading-relaxed" data-id="element-446">
                  A fine evento, gli alberi della community verranno piantati
                  nel Comune di Roma da EIIS in collaborazione con Roma
                  Capitale.
                </p>
              </div>
            </div>}
        </div>
      </div>

      {/* Bottom Button */}
      <div className="mt-auto pt-8 pb-4 transition-all duration-700 ease-out relative z-10" style={{
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
      transitionDelay: '200ms'
    }} data-id="element-447">
        <button onClick={next} className="w-full bg-transparent border border-[#C5D82D] text-white rounded-full py-3.5 font-medium text-base transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-448">
          {slide === totalSlides - 1 ? 'Iniziamo' : 'Avanti'}
          <ArrowRight className="w-4 h-4" data-id="element-449" />
        </button>
      </div>
    </div>;
}